import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, Check, X as XIcon, Star, Truck, Clock, RefreshCcw, Lock } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { Marquee } from "@/components/Marquee";
import PaymentIcons from "@/components/PaymentIcons";
import Seo from "@/components/Seo";
import { TRUST, COMPARISON, COLORS, FEATURES, WORN_BY, IMG, PRODUCT, TEAM_OFFER } from "@/data/site";
import { useCart } from "@/lib/cart";

/* ---------------- HERO ---------------- */
function Hero() {
  const ref = useRef(null);
  const [mounted, setMounted] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.08, 1.24]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 120);
    return () => clearTimeout(t);
  }, []);

  return (
    <section ref={ref} data-testid="hero" className="relative h-[100svh] min-h-[640px] overflow-hidden grain">
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img src={IMG.heroBg} alt="Stadion bei Nacht" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/40 to-ink" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/70 to-transparent" />
      </motion.div>

      <motion.div style={{ opacity }} className="relative z-10 h-full container-x flex flex-col justify-center">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={mounted ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="overline mb-6 flex items-center gap-3"
        >
          <span className="h-px w-10 bg-gold inline-block" /> Premium Performance Grip-Socken
        </motion.div>

        <MaskedLines
          start={mounted}
          className="font-heading uppercase text-white"
          lineClassName="text-5xl sm:text-7xl lg:text-8xl"
          lines={["Entwickelt für", <span key="l" className="text-gold">Leistung.</span>]}
          stagger={0.12}
        />
        <MaskedLines
          start={mounted}
          className="font-heading uppercase text-white mt-1"
          lineClassName="text-5xl sm:text-7xl lg:text-8xl"
          lines={["Gemacht für", "Champions."]}
          delay={0.24}
          stagger={0.12}
        />

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={mounted ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.7 }}
          className="mt-8 max-w-xl text-white/70 text-lg leading-relaxed"
        >
          {PRODUCT.tagline}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={mounted ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7, delay: 0.85 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <Link to="/product" data-testid="hero-shop-now" className="btn-primary">
            Jetzt shoppen <ArrowRight className="w-4 h-4" />
          </Link>
          <Link to="/shop" data-testid="hero-collection" className="btn-ghost">
            Kollektion ansehen
          </Link>
        </motion.div>
      </motion.div>

      <motion.div style={{ opacity }} className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-white/50">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown className="w-4 h-4 animate-bounce" />
      </motion.div>
    </section>
  );
}

/* ---------------- MARQUEE BAND ---------------- */
function BandMarquee() {
  const word = "Designed for Performance";
  const word2 = "Built for Champions";
  return (
    <div className="bg-ink border-y border-white/10 py-6">
      <Marquee>
        {[word, word2, word, word2].map((w, i) => (
          <div key={i} className="flex items-center gap-8 px-8">
            <span className="font-heading text-3xl md:text-5xl uppercase text-outline-dim">{w}</span>
            <Star className="w-5 h-5 text-gold fill-gold" />
          </div>
        ))}
      </Marquee>
    </div>
  );
}

/* ---------------- TRUST STRIP ---------------- */
function TrustStrip() {
  return (
    <section data-testid="trust-strip" className="bg-ink py-24 md:py-32">
      <div className="container-x">
        <Reveal>
          <p className="overline mb-4">Warum STARSOX</p>
          <h2 className="font-heading text-4xl md:text-6xl uppercase leading-[0.95] max-w-3xl">
            Performance in jedem Detail
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 rounded-3xl overflow-hidden border border-white/10">
          {TRUST.map((t, i) => {
            const Icon = t.icon;
            return (
              <Reveal key={t.title} delay={i * 0.05}>
                <div data-testid={`trust-${i}`} className="bg-carbon p-8 md:p-10 h-full group hover:bg-carbon2 transition-colors duration-500">
                  <Icon className="w-8 h-8 text-gold mb-6 transition-transform duration-500 group-hover:-translate-y-1" />
                  <h3 className="font-heading text-2xl uppercase tracking-wide mb-3">{t.title}</h3>
                  <p className="text-white/50 leading-relaxed text-sm">{t.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------- PRODUCT SHOWCASE ---------------- */
function ProductShowcase() {
  const { addItem } = useCart();
  const [sel, setSel] = useState(COLORS[0]);
  return (
    <section data-testid="product-showcase" className="bg-carbon border-y border-white/10">
      <div className="container-x grid lg:grid-cols-2 gap-0 lg:gap-20">
        <div className="relative py-14 lg:py-0">
          <div className="lg:sticky lg:top-0 lg:h-screen flex items-center">
            <Reveal className="w-full">
              <div className="relative rounded-[1.75rem] overflow-hidden border border-white/10 bg-ink">
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_38%,rgba(212,175,55,0.14),transparent_60%)]" />
                <motion.img
                  key={sel.key}
                  src={sel.img}
                  alt={`STARSOX Performance Grip Socks – ${sel.label}`}
                  loading="lazy"
                  decoding="async"
                  initial={{ opacity: 0, scale: 1.03 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="relative w-full h-[440px] md:h-[640px] object-contain"
                />
                <div className="absolute top-5 left-5 bg-white text-ink font-mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full">Bestseller</div>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="py-14 lg:py-40">
          <Reveal>
            <p className="overline mb-4">Das Produkt</p>
            <h2 className="font-heading text-4xl md:text-6xl uppercase leading-[0.95]">{PRODUCT.name}</h2>
            <div className="flex items-center gap-3 mt-6">
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-gold">Premium Performance</span>
              <span className="h-1 w-1 rounded-full bg-white/30" />
              <span className="text-white/50 text-sm">One Size · EU 39–46</span>
            </div>
            <p className="mt-6 text-white/60 leading-relaxed max-w-lg">
              Silikon-Grip-Pads für maximale Traktion, ein atmungsaktives Premium-Gestrick und eine
              präzise Passform. Für Fußball, Torwarte und jede Sportart mit explosiven Bewegungen.
            </p>
          </Reveal>

          {/* Color selection */}
          <Reveal delay={0.08}>
            <div className="mt-9">
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Farbe</span>
                <span className="text-sm text-white/70">{sel.label}</span>
              </div>
              <div className="flex items-center gap-4">
                {COLORS.map((c) => (
                  <button
                    key={c.key}
                    data-testid={`showcase-color-${c.key}`}
                    onClick={() => setSel(c)}
                    aria-label={c.label}
                    className={`relative h-11 w-11 rounded-full transition-all duration-300 ${sel.key === c.key ? "ring-2 ring-gold ring-offset-2 ring-offset-carbon" : "ring-1 ring-white/20 hover:ring-white/60"}`}
                    style={{ backgroundColor: c.hex }}
                  >
                    {sel.key === c.key && c.key === "white" && <Check className="w-4 h-4 text-ink absolute inset-0 m-auto" />}
                  </button>
                ))}
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.12}>
            <div className="mt-9 grid grid-cols-2 gap-y-6 gap-x-8 max-w-md">
              {[
                ["Preis", PRODUCT.price], ["Größe", `${PRODUCT.size} · ${PRODUCT.fits}`],
                ["Material", PRODUCT.material], ["Versand", "4,19 € · ab 50 € gratis"],
              ].map(([k, v]) => (
                <div key={k}>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">{k}</p>
                  <p className="text-white/90 text-sm">{v}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.16}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <button onClick={() => addItem(sel.key, sel.label, 1)} data-testid="showcase-add-cart" className="btn-primary">
                In den Warenkorb · {PRODUCT.price}
              </button>
              <Link to={`/product?color=${sel.key}`} data-testid="showcase-view" className="btn-ghost">Details ansehen</Link>
            </div>
            <div className="mt-6"><PaymentIcons compact testId="payment-icons-showcase" /></div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ---------------- WHY / COMPARISON ---------------- */
function Comparison() {
  return (
    <section data-testid="comparison" className="bg-ink py-24 md:py-32">
      <div className="container-x">
        <Reveal className="text-center mx-auto max-w-3xl">
          <p className="overline mb-4">Der Unterschied</p>
          <h2 className="font-heading text-4xl md:text-6xl uppercase leading-[0.95]">Gewöhnliche Socke<br /><span className="text-white/30">vs.</span> STARSOX</h2>
        </Reveal>

        <div className="mt-14 max-w-4xl mx-auto rounded-3xl border border-white/10 overflow-hidden">
          <div className="grid grid-cols-[1fr_auto_1fr] md:grid-cols-3 bg-carbon2">
            <div className="p-5 md:p-6 font-mono text-[11px] uppercase tracking-widest text-white/40">Gewöhnlich</div>
            <div className="p-5 md:p-6 text-center font-mono text-[11px] uppercase tracking-widest text-white/40 hidden md:block">Merkmal</div>
            <div className="p-5 md:p-6 text-right font-mono text-[11px] uppercase tracking-widest text-gold">STARSOX</div>
          </div>
          {COMPARISON.map((row, i) => (
            <Reveal key={row.feature} delay={i * 0.04}>
              <div data-testid={`comparison-row-${i}`} className="grid grid-cols-[1fr_auto_1fr] md:grid-cols-3 border-t border-white/10 items-center bg-ink hover:bg-carbon transition-colors">
                <div className="p-5 md:p-6 flex items-start gap-2 text-white/45 text-sm">
                  <XIcon className="w-4 h-4 mt-0.5 shrink-0 text-white/25" /> <span>{row.ordinary}</span>
                </div>
                <div className="px-2 md:px-6 text-center font-heading uppercase text-sm md:text-base text-white/80 whitespace-nowrap">{row.feature}</div>
                <div className="p-5 md:p-6 flex items-start justify-end gap-2 text-white text-sm text-right">
                  <span>{row.starsox}</span> <Check className="w-4 h-4 mt-0.5 shrink-0 text-gold" />
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- GRIP / ACTION ---------------- */
function GripSection() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <section ref={ref} data-testid="grip-section" className="relative h-[80vh] min-h-[520px] overflow-hidden grain">
      <motion.div style={{ y }} className="absolute inset-0 scale-125">
        <img src={IMG.grip} alt="Explosiver Antritt" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/60" />
      </motion.div>
      <div className="relative z-10 h-full container-x flex flex-col justify-center">
        <Reveal>
          <p className="overline mb-4">Grip = Vertrauen</p>
          <h2 className="font-heading text-5xl md:text-8xl uppercase leading-[0.9] max-w-4xl">
            Jeder Antritt.<br /><span className="text-gold">Voller Halt.</span>
          </h2>
          <p className="mt-6 max-w-xl text-white/70 leading-relaxed">
            Silikon-Grip-Pads verankern deinen Fuß im Schuh. Keine Millisekunde verloren –
            volle Kraftübertragung bei jedem Cut, Sprint und Antritt.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- COLOR PHILOSOPHY ---------------- */
function ColorPhilosophy() {
  return (
    <section data-testid="color-philosophy" className="bg-ink py-24 md:py-32">
      <div className="container-x">
        <Reveal className="max-w-3xl">
          <p className="overline mb-4">Farbphilosophie</p>
          <h2 className="font-heading text-4xl md:text-6xl uppercase leading-[0.95]">Drei Farben.<br />Drei Statements.</h2>
          <p className="mt-6 text-white/50 max-w-xl">Jede Farbe hat ihre eigene Persönlichkeit – wähle die, die zu deinem Spiel passt.</p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
          {COLORS.map((c, i) => (
            <Reveal key={c.key} delay={i * 0.08}>
              <Link to={`/product?color=${c.key}`} data-testid={`color-panel-${c.key}`} className="group block relative rounded-[1.75rem] overflow-hidden border border-white/10 h-[520px] bg-ink">
                <div className="pointer-events-none absolute inset-0 opacity-70 transition-opacity duration-500 group-hover:opacity-100" style={{ background: `radial-gradient(circle at 50% 40%, ${c.accent}22, transparent 62%)` }} />
                <img src={c.img} alt={`STARSOX ${c.label}`} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-contain transition-transform duration-700 group-hover:scale-[1.06]" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/10 to-transparent" />
                <div className="absolute top-6 left-6 flex gap-2">
                  <span className="h-4 w-4 rounded-full border border-white/50" style={{ backgroundColor: c.hex }} />
                </div>
                <div className="absolute bottom-0 inset-x-0 p-7">
                  <div className="flex flex-wrap gap-2 mb-3">
                    {c.words.map((w) => (
                      <span key={w} className="font-mono text-[10px] uppercase tracking-widest bg-white/10 backdrop-blur px-2.5 py-1 rounded-full">{w}</span>
                    ))}
                  </div>
                  <h3 className="font-heading text-4xl uppercase">{c.label}</h3>
                  <p className="text-white/70 text-sm mt-2 leading-relaxed">{c.philosophy}</p>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- SOCIAL PROOF ---------------- */
function SocialProof() {
  return (
    <section data-testid="social-proof" className="bg-carbon border-y border-white/10">
      {/* worn by marquee */}
      <div className="py-8 border-b border-white/10">
        <Marquee speed="animate-marquee-slow">
          {[...WORN_BY, ...WORN_BY, ...WORN_BY].map((w, i) => (
            <div key={i} className="flex items-center gap-6 px-8">
              <span className="font-heading text-2xl md:text-3xl uppercase text-white/70">{w}</span>
              <Star className="w-4 h-4 text-gold fill-gold" />
            </div>
          ))}
        </Marquee>
      </div>

      <div className="container-x py-24 md:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <div className="relative rounded-3xl overflow-hidden border border-white/10">
              <img src={IMG.match} alt="Nachwuchsspiel" loading="lazy" decoding="async" className="w-full h-[420px] object-cover" />
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="overline mb-4">Getragen auf dem Platz</p>
            <h2 className="font-heading text-4xl md:text-6xl uppercase leading-[0.95]">Vom Nachwuchs<br />bis zum Amateurplatz</h2>
            <p className="mt-6 text-white/60 leading-relaxed max-w-lg">
              Spieler aus Nachwuchsakademien wie dem <span className="text-white">FC Schalke 04</span> und dem
              <span className="text-white"> MSV Duisburg</span> haben STARSOX-Produkte bereits getragen.
            </p>
            <p className="mt-4 text-white/35 text-xs leading-relaxed max-w-lg">
              Hinweis: Es besteht keine offizielle Partnerschaft oder ein Sponsoring mit den genannten Vereinen.
              Die Namensnennung dient ausschließlich der Veranschaulichung.
            </p>
          </Reveal>
        </div>
      </div>

      {/* Sportfreunde Sümmern feature */}
      <div className="container-x pb-24 md:pb-32">
        <Reveal>
          <div className="rounded-3xl overflow-hidden border border-white/10 grid lg:grid-cols-2">
            <div className="p-10 md:p-14 flex flex-col justify-center bg-ink">
              <p className="overline mb-4">Team-Spotlight</p>
              <h3 className="font-heading text-3xl md:text-5xl uppercase leading-[0.95]">Sportfreunde Sümmern</h3>
              <p className="mt-5 text-white/60 leading-relaxed">
                Die Nachwuchsmannschaft der Sportfreunde Sümmern wurde mit STARSOX Performance Grip Socks
                ausgestattet. Wir freuen uns, den Verein auf dem Platz zu begleiten.
              </p>
              <Link to="/teams" data-testid="social-team-cta" className="btn-ghost mt-8 self-start">Team ausstatten</Link>
            </div>
            <div className="relative min-h-[320px]">
              <img src={IMG.team} alt="Sportfreunde Sümmern mit STARSOX" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover" />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- BRAND STATEMENT ---------------- */
function BrandStatement() {
  return (
    <section data-testid="brand-statement" className="bg-ink py-24 md:py-32">
      <div className="container-x">
        <Reveal className="max-w-4xl">
          <p className="overline mb-4">Das Versprechen</p>
          <h2 className="font-heading text-4xl md:text-7xl uppercase leading-[0.9]">
            Designed for Performance.<br /><span className="text-gold">Built for Champions.</span>
          </h2>
          <p className="mt-6 text-white/60 leading-relaxed text-lg max-w-2xl">
            Jedes Paar STARSOX ist auf ein Ziel ausgelegt: maximaler Halt, kompromissloser Komfort
            und volle Kontrolle in jeder Bewegung – vom ersten Antritt bis zum letzten Sprint.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap gap-3">
            {FEATURES.map((f) => (
              <span key={f} className="font-mono text-[11px] uppercase tracking-widest border border-white/12 bg-carbon px-4 py-2 rounded-full text-white/70">{f}</span>
            ))}
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- TEAMS CTA ---------------- */
function TeamsCTA() {
  return (
    <section data-testid="teams-cta" className="bg-ink pb-24 md:pb-32">
      <div className="container-x">
        <Reveal>
          <div className="relative rounded-[2rem] overflow-hidden border border-gold/30 bg-gradient-to-br from-carbon2 to-ink p-10 md:p-16">
            <div className="max-w-2xl">
              <p className="overline mb-4">Teams & Clubs</p>
              <h2 className="font-heading text-4xl md:text-7xl uppercase leading-[0.9]">
                {TEAM_OFFER.pairs} Paar. <span className="text-gold">{TEAM_OFFER.price}.</span>
              </h2>
              <p className="mt-5 text-white/60 leading-relaxed">
                Statt {TEAM_OFFER.regular} – 10 % Rabatt ab {TEAM_OFFER.pairs} Paar, automatisch im Warenkorb.
                Das perfekte Paket, um dein ganzes Team auszustatten –
                für einheitlichen Auftritt und maximalen Halt auf dem Platz.
              </p>
              <Link to="/teams" data-testid="teams-cta-btn" className="btn-primary mt-8">
                Jetzt Team bestellen <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- SERVICE / SHIPPING ---------------- */
function ServiceBand() {
  const items = [
    { icon: Truck, title: "Versand", desc: "4,19 € · ab 50 € kostenlos", sub: "Innerhalb Deutschlands" },
    { icon: Clock, title: "Lieferzeit", desc: "2–3 Werktage", sub: "Schnell & zuverlässig" },
    { icon: RefreshCcw, title: "Rückgabe", desc: "14 Tage Rückgaberecht", sub: "Ohne Risiko bestellen" },
    { icon: Lock, title: "Sichere Zahlung", desc: "Verschlüsselt via Stripe", sub: "Käuferschutz inklusive" },
  ];
  return (
    <section data-testid="service-band" className="bg-ink border-t border-white/10 py-16 md:py-20">
      <div className="container-x">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map((it, i) => {
            const Icon = it.icon;
            return (
              <Reveal key={it.title} delay={i * 0.05}>
                <div className="card-hair p-7 h-full">
                  <Icon className="w-6 h-6 text-gold mb-5" />
                  <h3 className="font-heading text-xl uppercase tracking-wide">{it.title}</h3>
                  <p className="text-white/85 text-sm mt-2">{it.desc}</p>
                  <p className="text-white/40 text-xs mt-1">{it.sub}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
        <Reveal delay={0.1}>
          <div className="mt-8 flex flex-col md:flex-row items-center justify-between gap-5 border border-white/10 rounded-2xl bg-carbon px-7 py-6">
            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50">Akzeptierte Zahlungsmethoden</p>
            <PaymentIcons testId="payment-icons-service" />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <div className="bg-ink">
      <Seo title="Premium Grip-Socken für maximale Performance" description="STARSOX Performance Grip Socks – maximaler Grip, Komfort und Kontrolle für Fußball, Torwarte und explosive Sportarten. Designed for Performance. Built for Champions." path="/" />
      <Hero />
      <BandMarquee />
      <TrustStrip />
      <ProductShowcase />
      <Comparison />
      <GripSection />
      <ColorPhilosophy />
      <SocialProof />
      <BrandStatement />
      <ServiceBand />
      <TeamsCTA />
    </div>
  );
}
